import { useState, useEffect, useCallback } from 'react';
import {
  Users, Search, Trash2, Power, Mail, Download,
  CheckCircle, XCircle, GraduationCap, RefreshCw,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_URL } from '@/services/apiClient';
import { dashboardService } from '@/services/dashboardService';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'student' | 'teacher' | 'admin';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

interface Stats { total: number; students: number; teachers: number; active: number; }

export function UserManagement({ dm = false }: { dm?: boolean }) {
  const [users, setUsers]               = useState<User[]>([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [roleFilter, setRoleFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [stats, setStats]               = useState<Stats>({ total: 0, students: 0, teachers: 0, active: 0 });

  // ── Stats: pull from the reliable getSchoolAdminStats endpoint ──────────────
  const fetchStats = useCallback(async () => {
    try {
      const s = await dashboardService.getSchoolAdminStats();
      setStats(prev => ({
        ...prev,
        students: s.totalStudents ?? prev.students,
        teachers: s.totalTeachers ?? prev.teachers,
        total:    (s.totalStudents ?? 0) + (s.totalTeachers ?? 0),
      }));
    } catch { /* silently fall back to counting from user list */ }
  }, []);

  // ── Users list ───────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('authToken');
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (roleFilter !== 'all')   params.set('role',   roleFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchTerm.trim())      params.set('search', searchTerm.trim());

      const res = await fetch(`${API_URL}/dashboard/school-admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        const list: User[] = data.users || [];
        setUsers(list);
        setTotalPages(data.pagination?.totalPages ?? 1);

        // Try to read stats from the list response with flexible field name mapping
        const s = data.stats;
        if (s) {
          setStats({
            total:    s.total    ?? s.totalUsers  ?? 0,
            students: s.students ?? s.totalStudents ?? 0,
            teachers: s.teachers ?? s.totalTeachers ?? 0,
            active:   s.active   ?? s.totalActive   ?? list.filter(u => u.isActive).length,
          });
        } else {
          // Fallback: count from this page's data (accurate for active at least)
          setStats(prev => ({ ...prev, active: list.filter(u => u.isActive).length }));
        }
      }
    } catch { /* silently fail */ } finally {
      setLoading(false);
    }
  }, [page, roleFilter, statusFilter, searchTerm]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const toggleStatus = async (userId: string, current: boolean) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('authToken')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !current }),
      });
      if (res.ok) { fetchUsers(); fetchStats(); }
    } finally { setActionLoading(null); }
  };

  const deleteUser = async (userId: string) => {
    if (!confirm('Delete this user? This cannot be undone.')) return;
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` },
      });
      if (res.ok) { fetchUsers(); fetchStats(); }
    } finally { setActionLoading(null); }
  };

  const resendInvite = async (userId: string, email: string) => {
    setActionLoading(userId);
    try {
      const res = await fetch(`${API_URL}/dashboard/school-admin/users/${userId}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${sessionStorage.getItem('authToken')}` },
      });
      if (res.ok) alert(`Invitation resent to ${email}`);
    } finally { setActionLoading(null); }
  };

  const handleExport = () => {
    const escape = (f: string) =>
      (f.includes(',') || f.includes('"') || f.includes('\n')) ? `"${f.replace(/"/g, '""')}"` : f;
    const csv = [
      ['Name', 'Email', 'Role', 'Status', 'Created', 'Last Login'].map(escape).join(','),
      ...users.map(u => [
        u.name, u.email, u.role,
        u.isActive ? 'Active' : 'Inactive',
        new Date(u.createdAt).toLocaleDateString(),
        u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never',
      ].map(escape).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), { href: url, download: `users-${new Date().toISOString().split('T')[0]}.csv` }).click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // ── Styles ───────────────────────────────────────────────────────────────────
  const cardCls   = `border rounded-xl shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls  = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls   = dm ? 'text-slate-400' : 'text-slate-500';
  const mutedCls  = dm ? 'text-slate-500' : 'text-slate-400';
  const bodyTxt   = dm ? 'text-slate-200' : 'text-slate-700';
  const theadCls  = dm ? 'bg-slate-700/50' : 'bg-slate-50';
  const rowHover  = dm ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50';
  const dividerCls = dm ? 'divide-slate-700' : 'divide-slate-100';
  const borderCls  = dm ? 'border-slate-700' : 'border-slate-200';
  const inputCls   = `w-full pl-9 pr-3 py-2 text-sm border rounded-lg outline-none ${
    dm ? 'bg-slate-700/60 border-slate-600 text-slate-200 placeholder-slate-500 focus:border-slate-400'
       : 'bg-white border-slate-200 text-slate-700 placeholder-slate-400 focus:border-slate-400'
  }`;

  const STAT_CARDS = [
    { label: 'Total Users',  value: stats.total,    sub: 'All roles',       icon: Users,         iconBg: dm ? 'bg-slate-700'       : 'bg-slate-100',       iconColor: 'text-slate-500' },
    { label: 'Students',     value: stats.students,  sub: 'Enrolled',        icon: GraduationCap, iconBg: dm ? 'bg-blue-900/50'     : 'bg-blue-100',        iconColor: 'text-blue-500'  },
    { label: 'Teachers',     value: stats.teachers,  sub: 'Staff members',   icon: Users,         iconBg: dm ? 'bg-emerald-900/50'  : 'bg-emerald-100',     iconColor: 'text-emerald-500' },
    { label: 'Active',       value: stats.active,    sub: 'Currently active', icon: CheckCircle,  iconBg: dm ? 'bg-emerald-900/50'  : 'bg-emerald-100',     iconColor: 'text-emerald-500' },
  ];

  return (
    <div className="space-y-6">

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS.map(({ label, value, sub, icon: Icon, iconBg, iconColor }) => (
          <div key={label} className={`${cardCls} p-5`}>
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
                <Icon className={`h-5 w-5 ${iconColor}`} />
              </div>
              <div>
                <p className={`text-xs font-medium ${descCls}`}>{label}</p>
                <p className={`text-2xl font-bold ${titleCls}`}>{value}</p>
                <p className={`text-xs ${mutedCls}`}>{sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── User Table Card ── */}
      <div className={cardCls}>
        {/* Header */}
        <div className={`px-5 py-4 border-b ${borderCls} flex items-center justify-between gap-3 flex-wrap`}>
          <div>
            <h3 className={`text-base font-semibold ${titleCls}`}>User Management</h3>
            <p className={`text-sm ${descCls}`}>View and manage all users in your school</p>
          </div>
          <div className="flex items-center gap-2" data-dm={dm}>
            <button onClick={() => { fetchUsers(); fetchStats(); }} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button onClick={handleExport} className="btn-glass btn-glass-pill flex items-center gap-1.5 text-sm">
              <Download className="h-3.5 w-3.5" />
              Export
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`px-5 py-3 border-b ${borderCls} flex flex-wrap items-center gap-3`} data-dm={dm}>
          <div className="relative flex-1 min-w-[200px]">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none ${mutedCls}`} />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchUsers()}
              className={inputCls}
            />
          </div>
          <Select value={roleFilter} onValueChange={v => { setRoleFilter(v); setPage(1); }}>
            <SelectTrigger className="btn-glass btn-glass-pill w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="student">Students</SelectItem>
              <SelectItem value="teacher">Teachers</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="btn-glass btn-glass-pill w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dm ? 'border-slate-400' : 'border-slate-800'}`} />
          </div>
        ) : users.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-16 gap-3 ${mutedCls}`}>
            <Users className="h-10 w-10 opacity-40" />
            <p className="text-sm">No users found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={theadCls}>
                    {['Name', 'Email', 'Role', 'Status', 'Created', 'Last Login', 'Actions'].map(h => (
                      <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${dividerCls}`}>
                  {users.map(user => (
                    <tr key={user.id} className={`${rowHover} transition-colors`}>
                      <td className={`px-4 py-3 text-sm font-medium ${bodyTxt}`}>{user.name}</td>
                      <td className={`px-4 py-3 text-sm ${descCls}`}>{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border capitalize font-medium ${
                          dm ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-600'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          user.isActive
                            ? (dm ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                            : (dm ? 'bg-red-900/40 text-red-400'         : 'bg-red-100 text-red-700')
                        }`}>
                          {user.isActive
                            ? <><CheckCircle className="h-3 w-3" /> Active</>
                            : <><XCircle    className="h-3 w-3" /> Inactive</>}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-sm ${descCls}`}>{fmtDate(user.createdAt)}</td>
                      <td className={`px-4 py-3 text-sm ${descCls}`}>{user.lastLoginAt ? fmtDate(user.lastLoginAt) : 'Never'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            className="btn-glass btn-glass-pill p-1.5"
                            onClick={() => toggleStatus(user.id, user.isActive)}
                            disabled={actionLoading === user.id}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          >
                            <Power className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="btn-glass btn-glass-pill p-1.5"
                            onClick={() => resendInvite(user.id, user.email)}
                            disabled={actionLoading === user.id}
                            title="Resend Invite"
                          >
                            <Mail className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="btn-glass btn-glass-pill btn-glass-danger p-1.5"
                            onClick={() => deleteUser(user.id)}
                            disabled={actionLoading === user.id}
                            title="Delete User"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className={`flex items-center justify-between px-5 py-3 border-t ${borderCls}`}>
                <p className={`text-sm ${mutedCls}`}>Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button className="btn-glass text-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    Previous
                  </button>
                  <button className="btn-glass text-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
